/* eslint-disable no-underscore-dangle */
import { Observable, Subject, share, throwError } from "rxjs";
import { HubStatuses } from "@miplatform/types";
import {
  HubConnection,
  IHttpConnectionOptions,
  IHubProtocol,
} from "@microsoft/signalr";
import { createConnection } from "./createConnection";

export class SignalrHub {
  url: string;
  hubName: string;
  options?: IHttpConnectionOptions;
  automaticReconnect?: boolean | number[];
  withHubProtocol?: IHubProtocol;

  _connection: HubConnection | null = null;

  _startSubject = new Subject();

  _stopSubject = new Subject();

  _errorSubject = new Subject();

  _stateSubject = new Subject();

  _subjects = {};

  start$;

  stop$;

  state$;

  error$;

  get connectionID() {
    return this._connection?.connectionId ?? undefined;
  }

  constructor(
    hubName: string,
    url: string,
    options?: IHttpConnectionOptions,
    automaticReconnect?: boolean | number[],
    withHubProtocol?: IHubProtocol
  ) {
    this.hubName = hubName;
    this.url = url;
    this.options = options;
    this.automaticReconnect = automaticReconnect;
    this.withHubProtocol = withHubProtocol;
    this.start$ = this._startSubject.asObservable();
    this.stop$ = this._stopSubject.asObservable();
    this.state$ = this._stateSubject.asObservable();
    this.error$ = this._errorSubject.asObservable();
  }

  _ensureConnectionOpened = () => {
    if (!this._connection) {
      this._connection = createConnection(
        this.url,
        this.options,
        this.automaticReconnect,
        this.withHubProtocol
      );
      this._connection.onclose((error) => {
        if (error) {
          this._errorSubject.next(error);
        }
        this._stateSubject.next(HubStatuses.Disconnected);
      });
      this._connection.onreconnecting(() => {
        this._stateSubject.next(HubStatuses.Reconnecting);
      });
      this._connection.onreconnected(() => {
        this._stateSubject.next(HubStatuses.Reconnected);
      });
    }
    return this._connection;
  };

  start = () => {
    const connection = this._ensureConnectionOpened();
    connection
      .start()
      .then(() => {
        this._startSubject.next(null);
        this._stateSubject.next(HubStatuses.Connected);
      })
      .catch((error) => this._errorSubject.next(error));

    return this._startSubject.asObservable();
  };

  stop = () => {
    if (!this._connection) {
      return throwError(
        () =>
          new Error(
            "The connection has not been started yet. Please start the connection by invoking the start method before attempting to stop listening from the server."
          )
      );
    }
    this._connection
      .stop()
      .then(() => {
        this._stopSubject.next(null);
      })
      .catch((error) => this._errorSubject.next(error));
    return this._stopSubject.asObservable();
  };

  on = (eventName: string) =>
    new Observable((observer) => {
      const connection = this._ensureConnectionOpened();

      const callback = (data: any) => {
        observer.next(data);
      };

      connection.on(eventName, callback);

      const errorSubscription = this._errorSubject.subscribe(() => {
        observer.error(new Error(`The connection has been closed.`));
      });
      const stopSubscription = this._stopSubject.subscribe(() => {
        observer.complete();
      });

      return () => {
        errorSubscription.unsubscribe();
        stopSubscription.unsubscribe();
        connection.off(eventName, callback);
      };
    }).pipe(share());
}
